import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check } from "lucide-react";

const AddOnsSection = ({ onBookingClick, onEstimateClick }: { onBookingClick: () => void; onEstimateClick?: () => void }) => {
  const addons = [
    {
      id: "digital-labeling",
      label: "Digital Labeling",
      title: "Digital Labeling & Inventory",
      description:
        "Scannable labels linked to a digital inventory so you can find anything fast and update from your phone.",
      bullets: [
        "QR/NFC labels with unique codes",
        "Searchable, shareable inventory",
        "Photos, notes, and categories",
        "Works on iOS and Android",
      ],
    },
    {
      id: "smart-home",
      label: "Smart Home",
      title: "Smart Home Systems Integration",
      description:
        "Connect organization to automations and reminders that support daily routines and maintenance.",
      bullets: [
        "HomeKit, Google, and Alexa friendly",
        "Restock and maintenance reminders",
        "Sensors for key zones (pantry, utility)",
        "Voice shortcuts for household tasks",
      ],
    },
    {
      id: "professional-products",
      label: "Professional Products",
      title: "Professional Products & Sourcing",
      description:
        "Custom storage solutions and high-quality organizing products sourced specifically for your space.",
      bullets: [
        "Custom closet and storage systems",
        "Premium organizing containers and bins",
        "Professional labeling and signage",
        "Specialty hardware and accessories",
      ],
    },
    {
      id: "maintenance-plans",
      label: "Maintenance Plans",
      title: "Ongoing Maintenance Support",
      description:
        "Regular check-ins and tune-ups to keep your organized spaces functioning perfectly over time.",
      bullets: [
        "Monthly or quarterly maintenance visits",
        "Seasonal organization adjustments",
        "System updates and improvements",
        "Priority scheduling for touch-ups",
      ],
    },
  ];

  return (
    <section id="add-ons" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <header className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-4">Service Add‑Ons</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Enhance any package with digital labeling and smart home systems for organization that's effortless and lasting.
          </p>
        </header>

        {/* Tabbed add-ons for consistency with services section */}
        <Tabs defaultValue={addons[0].id} className="max-w-7xl mx-auto">
          <TabsList className="flex flex-wrap gap-2 justify-start md:justify-center">
            {addons.map((addon) => (
              <TabsTrigger key={addon.id} value={addon.id}>
                {addon.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {addons.map((addon) => (
            <TabsContent key={addon.id} value={addon.id}>
              <Card className="border-border hover-lift fade-in-up bg-gradient-to-br from-card to-accent/20 comfort-glow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-2xl font-serif font-medium">{addon.title}</CardTitle>
                  <p className="text-muted-foreground mt-2">{addon.description}</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {addon.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-1 shrink-0" />
                        <span className="text-sm">{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button onClick={onBookingClick} className="w-full">
                      Add to Booking
                    </Button>
                    <Button variant="outline" className="w-full" onClick={onEstimateClick}>
                      Request Estimate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default AddOnsSection;